import { useCallback, useEffect, useState } from "react";
import { alVencerSesion, api, ApiError, type Usuario } from "@/lib/api";

export type EstadoSesion =
  | { tipo: "cargando" }
  | { tipo: "anonimo" }
  | { tipo: "error"; mensaje: string }
  | { tipo: "logueado"; usuario: Usuario };

/** Estado de sesión de la app: consulta /api/auth/yo al arrancar y pasa a anónimo si la API responde 401. */
export function useSesion() {
  const [estado, setEstado] = useState<EstadoSesion>({ tipo: "cargando" });

  const verificar = useCallback(async () => {
    try {
      const usuario = await api.get<Usuario>("/api/auth/yo");
      setEstado({ tipo: "logueado", usuario });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setEstado({ tipo: "anonimo" });
      else setEstado({ tipo: "error", mensaje: e instanceof Error ? e.message : "Error inesperado" });
    }
  }, []);

  useEffect(() => {
    void verificar();
  }, [verificar]);

  // Un 401 a mitad de uso (sesión vencida o revocada) vuelve al login; App guarda la ruta para volver.
  useEffect(() => alVencerSesion(() => setEstado((e) => (e.tipo === "logueado" ? { tipo: "anonimo" } : e))), []);

  const alIniciar = useCallback((usuario: Usuario) => setEstado({ tipo: "logueado", usuario }), []);

  const cerrarSesion = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      setEstado({ tipo: "anonimo" });
    }
  }, []);

  return { estado, alIniciar, cerrarSesion, reintentar: verificar };
}
